/**
 * FilePicker — shows the individual-file chooser. That is its entire job.
 * The folder chooser is DirectoryPicker's; keeping them apart keeps each to one.
 */

import { classify } from '../domain/media-type';

type PickerWindow = Window & {
  showOpenFilePicker?: (opts?: {
    multiple?: boolean;
    excludeAcceptAllOption?: boolean;
    id?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<FileSystemFileHandle[]>;
};

/** Offered in the picker's filter dropdown. Matches what the scanner accepts. */
const MEDIA_TYPES = [
  {
    description: 'Photos and video',
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.tif', '.tiff', '.heic', '.heif'],
      'video/*': ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v'],
    },
  },
];

export class FilePicker {
  static get isSupported(): boolean {
    return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
  }

  /**
   * Returns null when the photographer dismisses the dialog.
   * Anything that is not media m3cull handles is dropped here rather than
   * being registered and then failing at thumbnail time.
   */
  async pick(id?: string): Promise<FileSystemFileHandle[] | null> {
    const picker = (window as PickerWindow).showOpenFilePicker;
    if (!picker) throw new Error('This browser cannot open individual files.');

    try {
      const handles = await picker({ multiple: true, id, types: MEDIA_TYPES });
      const media = handles.filter((handle) => classify(handle.name) !== null);
      return media.length > 0 ? media : [];
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      throw err;
    }
  }
}
